import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MapPin, AlertTriangle } from "lucide-react";
import { ON_SITE_RADIUS_M, formatDistance } from "@/lib/geoUtils";
import type { JobCandidate } from "@/hooks/useTimeClock";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  candidates: JobCandidate[];
  best: JobCandidate | null;
  onConfirm: (jobId: string, offSiteReason?: string) => Promise<void> | void;
  onRejectSuggested?: () => void;
  busy?: boolean;
}

export function JobConfirmDialog({ open, onOpenChange, candidates, best, onConfirm, onRejectSuggested, busy }: Props) {
  const [selectedId, setSelectedId] = useState<string>("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedId(best?.id || "");
      setReason("");
    }
  }, [open, best?.id]);

  const selected = candidates.find((c) => c.id === selectedId);
  const hasJobCoordinates = selected?.latitude != null && selected?.longitude != null;
  const offSite = hasJobCoordinates && selected?.distance_m != null
    ? selected.distance_m > ON_SITE_RADIUS_M
    : false;
  const needsReason = offSite;
  const canConfirm = !!selectedId && (!needsReason || reason.trim().length > 3);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Confirm your job site
          </DialogTitle>
          <DialogDescription>
            We auto-picked the closest job scheduled for you today. Confirm it or tell dispatch it is wrong.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Suggested job</Label>
            {candidates.length === 0 ? (
              <div className="mt-2 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                No job is assigned to you for today. Dispatch needs to assign today’s job before you can clock in.
              </div>
            ) : (
              <div className="mt-2 rounded-md border border-border bg-muted/20 p-3 text-sm">
                <div className="font-medium">{selected?.property_address || best?.property_address}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {selected?.distance_m != null && (
                    <Badge
                      variant={selected.distance_m <= ON_SITE_RADIUS_M ? "default" : "outline"}
                      className="text-[10px]"
                    >
                      {formatDistance(selected.distance_m)} away
                    </Badge>
                  )}
                  <span>
                    {selected?.distance_m == null
                      ? hasJobCoordinates
                        ? "GPS distance unavailable for this job."
                        : "This job has no saved map pin yet, so GPS cannot verify distance."
                      : `Closest scheduled job based on your location.`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {selected && (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-sm">
              <div className="font-medium">{selected.property_address}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {selected.distance_m == null
                  ? hasJobCoordinates
                    ? "GPS distance is still being determined."
                    : "No job coordinates are saved for this address yet."
                  : `You're ${formatDistance(selected.distance_m)} from this site.`}
              </div>
            </div>
          )}

          {offSite && selected && (
            <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-warning-foreground">
                <AlertTriangle className="h-4 w-4 text-warning" />
                You're not at the site — tell us why
              </div>
              <Textarea
                placeholder="e.g. Picking up materials, on the way, supply run…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          {!!selectedId && !!onRejectSuggested && (
            <Button variant="outline" onClick={onRejectSuggested} disabled={busy}>
              Wrong job
            </Button>
          )}
          <Button
            disabled={!canConfirm || busy}
            onClick={() => onConfirm(selectedId, needsReason ? reason.trim() : undefined)}
          >
            {busy ? "Clocking in…" : "Clock in"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
